import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { dispatchMfeMessage, MfeMessageHandlers, mfeNavigateMessageType } from "@webcore/services/mfe/mfeMessaging.ts";

/**
 * Listens for navigate messages from the parent app and performs client-side navigation. This lets
 * the parent switch the embedded view (e.g. between custom views) without reloading the iframe, so
 * the read-only session and data cache established by MfeBootstrap stay warm across navigations.
 *
 * Mounted outside MfeBootstrap's "ready" gate so it always listens: a navigate that arrives before
 * the session is established simply updates the URL, which the routes then match once they mount.
 * Renders nothing.
 */
function MfeNavigationListener() {
  const navigate = useNavigate();

  useEffect(() => {
    const handlers: MfeMessageHandlers = {
      [mfeNavigateMessageType]: (message) => navigate(message.path),
    };

    const window_message = (event: MessageEvent) => {
      // Accept navigation only from the parent window. The path is a route (no token), and the
      // session token is validated server-side, so we don't pin a single parent origin here.
      if (event.source !== window.parent) {
        return;
      }

      dispatchMfeMessage(event, handlers);
    };

    window.addEventListener("message", window_message);
    return () => window.removeEventListener("message", window_message);
  }, [navigate]);

  return null;
}

export default MfeNavigationListener;
